import { useCallback } from "react";

import {
  type BulkAction,
  isBulkActionRunning,
  startDownload,
  useBulkActions,
} from "@punto/frontend-shared/components/bulk-actions";

/** The three print bundles built on the worker (P-3022), by workflow type. */
export const PRINT_BUNDLE_TYPE = {
  collegeFolder: "print_college_folder_bundle",
  deliveryTracking: "print_delivery_tracking_bundle",
  deliveryTrackingProcedure: "print_delivery_tracking_procedure_bundle",
} as const;

/** The worker's `errorMessage` for a bundle with no documents to print. */
export const PRINT_BUNDLE_EMPTY = "PRINT_BUNDLE_EMPTY";

/**
 * Whether a print of `type` for an entity is still being built in this tab.
 * The enqueue answers in milliseconds but the build takes seconds: the print
 * affordances stay busy for the whole build so a second click does not queue a
 * second bundle and a second download.
 */
export const useIsPrinting = (type: string) => {
  const { actions } = useBulkActions();

  return useCallback(
    (referenceId: string) =>
      actions.some(
        (action) =>
          action.type === type &&
          action.referenceId === referenceId &&
          isBulkActionRunning(action),
      ),
    [actions, type],
  );
};

interface PrintBundleOutcome {
  /** The PDF was saved. */
  onDownloaded?: (action: BulkAction) => void;
  /**
   * The build failed — `errorMessage` is the worker's code — or the finished
   * PDF could not be fetched, in which case it is `undefined`.
   */
  onFailed: (errorMessage?: string) => void;
}

/**
 * What every print bundle does as it settles (P-3022): a finished one downloads
 * itself, a failed one is reported. Each print type's `onSettled` calls this
 * with its own outcome — which messages it shows, what "printed" means for it.
 */
export const settlePrintBundle = (
  action: BulkAction,
  { onDownloaded, onFailed }: PrintBundleOutcome,
): void => {
  if (action.status === "failed") {
    onFailed(action.errorMessage);
    return;
  }
  if (action.status !== "completed" || !action.downloadUrl) return;

  startDownload(action.downloadUrl).then(
    () => onDownloaded?.(action),
    () => onFailed(),
  );
};
